import React, { useEffect, useState, useMemo } from "react";
import { getDirectusClient } from "../../lib/directus";
import {
  Table,
  Button,
  Spin,
  Row,
  Col,
  List,
  DatePicker,
  Space,
  Tag,
} from "antd";
import { readUsers, readItems, readMe } from "@directus/sdk";
import Layouts from "../../components/Layouts";
import { useSelector } from "react-redux";
import moment from "moment";
import validarpermiso from "../../lib/utils";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import InfiniteScroll from "react-infinite-scroll-component";
import { Divider, Skeleton } from "antd";

const customPopupStyle = {
  vendorPopup: {
    backgroundColor: "#2196f3",
    color: "white",
    padding: "10px 15px",
    borderRadius: "5px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  },
  clientPopup: {
    backgroundColor: "#f44336",
    color: "white",
    padding: "10px 15px",
    borderRadius: "5px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  },
  title: {
    fontSize: "16px",
    fontWeight: "bold",
    marginBottom: "5px",
  },
  info: {
    fontSize: "14px",
  },
  status: {
    padding: "4px 8px",
    borderRadius: "3px",
    backgroundColor: "rgba(255,255,255,0.2)",
    marginTop: "8px",
    display: "inline-block",
  },
};

// Keep the createCustomIcon and vendorIcon for vendors
const createCustomIcon = (svg, color) => {
  return L.divIcon({
    className: "custom-icon",
    html: svg,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });
};

const vendorIcon = createCustomIcon(
  `
  <svg width="40px" height="40px" viewBox="-1 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <g id="user" transform="translate(-3 -2)">
      <path id="secondary" fill="#2196f3" d="M9,15h6a5,5,0,0,1,5,5h0a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1H4a5,5,0,0,1,5-5Z"/>
      <path id="primary" d="M20,20h0a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1H4a5,5,0,0,1,5-5h6A5,5,0,0,1,20,20ZM12,3a4,4,0,1,0,4,4A4,4,0,0,0,12,3Z" fill="none" stroke="#ffffff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
    </g>
  </svg>
  `,
  "#2196f3"
);

// Create default colored markers for clients
const clientIcons = {
  activo: L.icon({
    iconUrl:
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  }),
  "gestion iniciada": L.icon({
    iconUrl:
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-yellow.png",
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  }),
  gestionado: L.icon({
    iconUrl:
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  }),
  "cliente ausente": L.icon({
    iconUrl:
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png",
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  }),
};

const weekdayMap = {
  monday: { id: 1, name: "Lunes" },
  tuesday: { id: 2, name: "Martes" },
  wednesday: { id: 3, name: "Miércoles" },
  thursday: { id: 4, name: "Jueves" },
  friday: { id: 5, name: "Viernes" },
  saturday: { id: 6, name: "Sábado" },
  sunday: { id: 7, name: "Domingo" },
};

const getMapCenter = (selectedVendedor, gestionRutero) => {
  // Si hay vendedor seleccionado, usar sus coordenadas
  if (selectedVendedor?.ultima_coordenada) {
    const [lat, lng] = selectedVendedor.ultima_coordenada
      .split(",")
      .map((coord) => parseFloat(coord.trim()));

    if (!isNaN(lat) && !isNaN(lng)) {
      return [lat, lng];
    }
  }

  // Si hay gestiones, usar el centro de todas las ubicaciones
  if (gestionRutero.length > 0) {
    const lats = gestionRutero.map((g) => parseFloat(g["570a31e0"].latitude));
    const lngs = gestionRutero.map((g) => parseFloat(g["570a31e0"].longitude));

    const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;
    const centerLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;

    if (!isNaN(centerLat) && !isNaN(centerLng)) {
      return [centerLat, centerLng];
    }
  }

  // Coordenadas por defecto (Bogotá)
  return [4.60971, -74.08175];
};

const Reportemaps = () => {
  const [vendedores, setVendedores] = useState([]);
  const userBodega = useSelector((state) => state.userdata.bodega?.id);
  const [selectedVendedor, setSelectedVendedor] = useState(null);
  const [mapKey, setMapKey] = useState(0);
  const [loadingVendedores, setLoadingVendedores] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const permisos = useSelector((state) => state.permisos);
  const [gestionRutero, setGestionRutero] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedDate, setSelectedDate] = useState(moment());
  const userRoleID = useSelector((state) => state.userdata.role.name);
  const validateSession = async () => {
    try {
      const directus = await getDirectusClient();
      await directus.request(readMe());
      return true;
    } catch (error) {
      window.location.reload();
      return false;
    }
  };

  const getVendedores = async (pageNumber = 1) => {
    setLoadingVendedores(true);

    if (await validateSession()) {
      try {
        const directus = await getDirectusClient();
        const response = await directus.request(
          readUsers({
            fields: ["*", "role.*"],
            filter: {
              _and: [{ role: { name: { _eq: "vendedor rutas" } } }],
              ...(userRoleID !== "adminapp" && userRoleID !== "Administrator"
                ? {
                    bodega: {
                      id: { _eq: userBodega },
                    },
                  }
                : {}),
            },
            limit: 10,
            page: pageNumber,
          })
        );

        if (response.length < 10) {
          setHasMore(false);
        }

        if (pageNumber === 1) {
          setVendedores(response);
        } else {
          setVendedores((prev) => [...prev, ...response]);
        }

        setPage(pageNumber);
      } catch (error) {
        console.error("Error fetching vendedores:", error);
      } finally {
        setLoadingVendedores(false);
      }
    }
  };

  const getClientesRuta = async (vendedorId) => {
    setLoadingClientes(true);
    if (await validateSession()) {
      try {
        const directus = await getDirectusClient();
        const dayName = selectedDate.format("dddd").toLowerCase();
        const dayId = weekdayMap[dayName].id;

        const vendorData = await directus.request(
          readUsers({
            fields: ["id", "first_name", "last_name", "ultima_coordenada"],
            filter: { id: { _eq: vendedorId } },
          })
        );

        const gestionData = await directus.request(
          readItems("gestion_cliente_rutero", {
            fields: [
              "id",
              "date_created",
              "4d1bc69a",
              "facturacion",
              "id",
              "vendedor.first_name",
              "406b9912.estado",
              "334b85fa.id",
              "570a31e0.latitude",
              "570a31e0.longitude",
              "1a4578d2.first_name",
            ],
            alias: {
              "4d1bc69a": "estado",
              "406b9912": "estado",
              "334b85fa": "cliente",
              "570a31e0": "cliente",
              "1a4578d2": "cliente",
            },
            filter: {
              _and: [
                {
                  vendedor: { _eq: vendedorId },
                },
                {
                  vendedor: {
                    circuitos_vendedor: {
                      dias: {
                        dias_id: {
                          id: {
                            _eq: dayId,
                          },
                        },
                      },
                    },
                  },
                },
              ],
              ...(userRoleID !== "adminapp" && userRoleID !== "Administrator"
                ? {
                    bodega: {
                      id: { _eq: userBodega },
                    },
                  }
                : {}),
            },
            sort: ["id"],
            limit: 25,
            page: 1,
          })
        );

        const validGestion = gestionData.filter(
          (item) =>
            item["570a31e0"] &&
            item["570a31e0"].latitude &&
            item["570a31e0"].longitude
        );

        setSelectedVendedor(vendorData[0]);
        setGestionRutero(validGestion);
        setMapKey((prevKey) => prevKey + 1);
      } catch (error) {
        console.error("Error fetching gestion rutero:", error);
      } finally {
        setLoadingClientes(false);
      }
    }
  };

  const loadMoreData = () => {
    if (loadingVendedores) return;
    getVendedores(page + 1);
  };

  useEffect(() => {
    getVendedores();
  }, []);

  const getClientIcon = (estado) => {
    const status = estado?.toLowerCase();
    return clientIcons[status] || clientIcons["cliente ausente"];
  };

  return (
    <Layouts>
      {validarpermiso("rutas", "read", permisos) ? (
        <Row gutter={[24, 24]} style={{ padding: "24px" }}>
          <Col xs={24} lg={8}>
            <Space
              direction="vertical"
              size="middle"
              style={{ marginBottom: 16 }}
            >
              <Space align="center">
                <DatePicker
                  onChange={(date) => {
                    setSelectedDate(date || moment());
                    if (selectedVendedor) {
                      getClientesRuta(selectedVendedor.id);
                    }
                  }}
                  format="DD/MM/YYYY"
                  allowClear={false}
                />
                <Tag color="blue">
                  {weekdayMap[selectedDate.format("dddd").toLowerCase()].name}
                </Tag>
              </Space>
              <h3>Vendedores</h3>
            </Space>
            <div
              id="scrollableVendors"
              style={{
                height: "calc(100vh - 250px)",
                overflow: "auto",
                backgroundColor: "white",
                padding: "16px",
                borderRadius: "8px",
              }}
            >
              <InfiniteScroll
                dataLength={vendedores.length}
                next={loadMoreData}
                hasMore={hasMore}
                loader={<Skeleton avatar paragraph={{ rows: 1 }} active />}
                endMessage={<Divider plain>No hay más usuarios</Divider>}
                scrollableTarget="scrollableVendors"
              >
                <List
                  itemLayout="horizontal"
                  dataSource={vendedores}
                  renderItem={(vendedor) => (
                    <List.Item
                      key={vendedor.id}
                      actions={[
                        <Button
                          key="view-map"
                          type="primary"
                          onClick={() => getClientesRuta(vendedor.id)}
                        >
                          Ver en el Mapa
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        title={`${vendedor.first_name} ${vendedor.last_name}`}
                        description={vendedor.email}
                      />
                    </List.Item>
                  )}
                />
              </InfiniteScroll>
            </div>
          </Col>

          <Col xs={24} lg={16}>
            <h3>Mapa de Gestión de Clientes</h3>
            <Spin spinning={loadingClientes}>
              <MapContainer
                key={mapKey}
                center={getMapCenter(selectedVendedor, gestionRutero)}
                zoom={13}
                style={{
                  height: "500px",
                  width: "100%",
                  borderRadius: "8px",
                  border: "1px solid #f0f0f0",
                }}
                dragging={true}
                touchZoom={false}
                scrollWheelZoom={true}
                doubleClickZoom={false}
                boxZoom={true}
                keyboard={false}
                zoomControl={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {selectedVendedor?.ultima_coordenada && (
                  <Marker
                    position={selectedVendedor.ultima_coordenada
                      .split(",")
                      .map((coord) => parseFloat(coord))}
                    icon={vendorIcon}
                  >
                    <Popup className="vendor-popup">
                      <div style={customPopupStyle.vendorPopup}>
                        <div style={customPopupStyle.title}>🚶‍♂️ Vendedor</div>
                        <div style={customPopupStyle.info}>
                          <div>
                            {selectedVendedor.first_name}{" "}
                            {selectedVendedor.last_name}
                          </div>
                          <small>
                            Última conexion:{" "}
                            {moment(selectedVendedor.date_updated).format(
                              "DD/MM/YYYY HH:mm"
                            )}
                            <br />
                            Ubicacion:{" "}
                            {selectedVendedor.ultima_coordenada
                              .split(",")
                              .map((coord) => parseFloat(coord))
                              .join(", ")}
                          </small>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                )}
                {gestionRutero.map((gestion) => (
                  <Marker
                    key={gestion.id}
                    position={[
                      parseFloat(gestion["570a31e0"].latitude),
                      parseFloat(gestion["570a31e0"].longitude),
                    ]}
                    icon={getClientIcon(gestion["406b9912"].estado)}
                  >
                    <Popup>
                      <div
                        style={{
                          ...customPopupStyle.clientPopup,
                          backgroundColor:
                            gestion["406b9912"].estado === "activo"
                              ? "rgb(54, 168, 244)"
                              : gestion["406b9912"].estado ===
                                "gestion iniciada"
                              ? "#FFC107"
                              : gestion["406b9912"].estado === "gestianado"
                              ? "rgb(28, 231, 6)"
                              : "#f44336",
                        }}
                      >
                        <div style={customPopupStyle.title}>🧍‍♂️ Cliente</div>
                        <div style={customPopupStyle.info}>
                          <div>{gestion["1a4578d2"].first_name}</div>
                          <div style={customPopupStyle.status}>
                            Estado: {gestion["406b9912"].estado}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              marginTop: "8px",
                              color: "#fff",
                            }}
                          >
                            Fecha:{" "}
                            {moment(gestion.date_created).format(
                              "DD/MM/YYYY HH:mm"
                            )}
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {selectedVendedor?.ultima_coordenada &&
                  gestionRutero.length > 0 && (
                    <Polyline
                      positions={[
                        selectedVendedor.ultima_coordenada
                          .split(",")
                          .map((coord) => parseFloat(coord)),
                        ...gestionRutero.map((gestion) => [
                          parseFloat(gestion["570a31e0"].latitude),
                          parseFloat(gestion["570a31e0"].longitude),
                        ]),
                      ]}
                      color="blue"
                    />
                  )}
              </MapContainer>
            </Spin>
          </Col>
        </Row>
      ) : (
        <h3>No tiene permisos para ver rutas</h3>
      )}
    </Layouts>
  );
};
export default Reportemaps;
